import { cva, type VariantProps } from 'class-variance-authority';
import { X, Info, CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../../lib/utils';
import { Alert, AlertTitle, AlertDescription } from '../../ui/alert';

import { alertCoreVariants } from './variants';

// Icon colors - using design system colors
const iconVariants = cva('w-5 h-5 flex-shrink-0', {
  variants: {
    type: {
      info: 'text-info',
      success: 'text-success',
      warning: 'text-amber-600',
      error: 'text-destructive',
    },
  },
  defaultVariants: {
    type: 'info',
  },
});

// Title uses foreground color from design system
const titleVariants = cva('text-foreground');

// Description uses muted-foreground from design system
const descriptionVariants = cva('text-muted-foreground');

// Dismiss button uses muted-foreground with hover state
const dismissVariants = cva(
  'w-6 h-6 cursor-pointer transition-colors text-muted-foreground hover:text-foreground',
);

const typeIcons = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
} as const;

export interface AlertCoreProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertCoreVariants> {
  /** Alert type - determines icon and icon color */
  type?: 'info' | 'success' | 'warning' | 'error';
  /** Alert title text */
  title: string;
  /** Alert description text */
  description?: string;
  /** Whether to show the dismiss button */
  dismissible?: boolean;
  /** Custom icon component (overrides default type icon) */
  icon?: React.ComponentType<{ className?: string }>;
  /** Callback when dismiss button is clicked */
  onDismiss?: () => void;
}

const AlertCore = React.forwardRef<HTMLDivElement, AlertCoreProps>(
  (
    {
      className,
      type = 'info',
      title,
      description,
      dismissible = true,
      icon: CustomIcon,
      onDismiss,
      ...props
    },
    ref,
  ) => {
    const IconComponent = CustomIcon || typeIcons[type];

    return (
      <Alert
        ref={ref}
        className={cn(alertCoreVariants(), className)}
        {...props}
      >
        <div className="flex items-start gap-3">
          {/* Icon - only element with type-specific color */}
          <IconComponent className={iconVariants({ type })} />

          {/* Content - uses design system colors */}
          <div className="flex-1 space-y-1">
            <AlertTitle
              className={cn(
                'text-text-md font-bold leading-6',
                titleVariants(),
              )}
            >
              {title}
            </AlertTitle>
            {description && (
              <AlertDescription
                className={cn('text-text-sm leading-5', descriptionVariants())}
              >
                {description}
              </AlertDescription>
            )}
          </div>

          {/* Dismiss Button - uses design system colors */}
          {dismissible && (
            <button
              type="button"
              onClick={onDismiss}
              className={dismissVariants()}
              aria-label="Dismiss alert"
            >
              <X className="w-full h-full" />
            </button>
          )}
        </div>
      </Alert>
    );
  },
);

AlertCore.displayName = 'AlertCore';

export { AlertCore };
